import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, InputNumber, Switch } from "antd";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import ConfirmationModal from "../../components/global/modal/ConfirmationModal.jsx";
import getEducationSectionsColumns from "../../columns/educationSections.js";
import {
  GetEducationSectionsApi,
  AddUpdateEducationSectionApi,
  DeleteEducationSectionApi,
} from "../../api/request/education.js";
import toast from "react-hot-toast";

export default function EducationSections() {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const getEducationSections = async () => {
    if (!materialId) return;
    try {
      setIsLoading(true);
      const response = await GetEducationSectionsApi(materialId);
      const responseData = response?.data;
      if (responseData?.success) {
        const mappedData = (responseData?.data || []).map((item) => ({ ...item, key: item?.id }));
        setData(mappedData);
      }
    } catch (error) {
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) return;
    try {
      setIsDeleteLoading(true);
      const response = await DeleteEducationSectionApi(itemToDelete.id);
      if (response?.data?.success) {
        toast.success(response?.data?.message || "Education section deleted successfully.");
        setDeleteModalOpen(false);
        setItemToDelete(null);
        getEducationSections();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openAddModal = () => {
    setSelectedItem(null);
    form.setFieldsValue({ title: "", units: 0, displayOrder: 0, isActive: true });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    form.setFieldsValue({
      title: item?.title,
      units: item?.units ?? 0,
      displayOrder: item?.displayOrder ?? 0,
      isActive: item?.isActive ?? true,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitLoading(true);
      const payload = {
        materialId,
        title: values?.title,
        units: Number(values?.units) ?? 0,
        isActive: values?.isActive ?? true,
      };
      if (selectedItem?.id) {
        payload.id = selectedItem.id;
        payload.displayOrder = Number(values?.displayOrder) ?? 0;
      }
      const response = await AddUpdateEducationSectionApi(payload);
      if (response?.data?.success) {
        toast.success(
          response?.data?.message ||
          (selectedItem
            ? "Education section updated successfully."
            : "Education section added successfully.")
        );
        handleCloseModal();
        getEducationSections();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong.");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const openViewUnits = (item) => {
    navigate(`/education/section/${item?.id}`, { state: { materialId } });
  };

  const columns = useMemo(
    () => getEducationSectionsColumns(openViewUnits, openEditModal, openDeleteModal),
    []
  );

  useEffect(() => {
    getEducationSections();
  }, [materialId]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedItem(null);
      form.resetFields();
    }
  }, [isModalOpen]);

  return (
    <div>
      <div className="flex justify-end items-center flex-wrap gap-5 mb-5">
        <button
          onClick={openAddModal}
          className="flex items-center bg-primary text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover duration-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              fillRule="evenodd"
              d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
              clipRule="evenodd"
            />
          </svg>
          Add Section
        </button>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            page={1}
            pageSize={10}
          />
        </div>
      </div>

      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={handleCloseModal}
        title={selectedItem ? "Edit Education Section" : "Add Education Section"}
        width={600}
      >
        <Form
          className="w-full"
          autoComplete="off"
          form={form}
          initialValues={{ title: "", units: 0, displayOrder: 0, isActive: true }}
          onFinish={handleSubmit}
        >
          <div className="grid sm:grid-cols-1 gap-5">
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Title</label>
              <Form.Item
                name="title"
                rules={[
                  { required: true, message: "Please enter a title." },
                  { whitespace: true, message: "Title cannot be empty or contain only spaces." },
                  { max: 255, message: "Title cannot exceed 255 characters." },
                ]}
              >
                <Input placeholder="Title" className="mt-1 w-full py-2" maxLength={255} showCount />
              </Form.Item>
            </div>
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Units</label>
              <Form.Item
                name="units"
                rules={[
                  { required: true, message: "Please enter units." },
                  {
                    type: "number",
                    min: 0,
                    message: "Units must be a positive number.",
                  },
                  {
                    validator: (_, value) => {
                      if (value != null && value !== "" && !Number.isInteger(Number(value))) {
                        return Promise.reject(new Error("Units must be a whole number."));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  placeholder="Units"
                  className="mt-1 w-full py-2"
                  min={0}
                  precision={0}
                  controls
                  style={{ width: "100%" }}
                  parser={(value) => {
                    const v = String(value ?? "").replace(/[^\d]/g, "");
                    return v === "" ? "" : parseInt(v, 10);
                  }}
                  onKeyDown={(e) => {
                    const allowed = ["Backspace", "Tab", "Delete", "ArrowLeft", "ArrowRight", "Home", "End"];
                    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) return;
                    if (!/^\d$/.test(e.key)) e.preventDefault();
                  }}
                />
              </Form.Item>
            </div>
            {selectedItem && (
              <div className="input-white">
                <label className="text-description text-[#2F2B3DCC] font-medium">Display Order</label>
                <Form.Item
                  name="displayOrder"
                  rules={[
                    { required: true, message: "Please enter display order." },
                    {
                      type: "number",
                      min: 0,
                      message: "Display order must be a positive number.",
                    },
                    {
                      validator: (_, value) => {
                        if (value != null && value !== "" && !Number.isInteger(Number(value))) {
                          return Promise.reject(new Error("Display order must be a whole number."));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Display Order"
                    className="mt-1 w-full py-2"
                    min={0}
                    precision={0}
                    controls
                    style={{ width: "100%" }}
                    parser={(value) => {
                      const v = String(value ?? "").replace(/[^\d]/g, "");
                      return v === "" ? "" : parseInt(v, 10);
                    }}
                    onKeyDown={(e) => {
                      const allowed = ["Backspace", "Tab", "Delete", "ArrowLeft", "ArrowRight", "Home", "End"];
                      if (allowed.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                  />
                </Form.Item>
              </div>
            )}
            <div className="input-white">
              <label className="text-description text-[#2F2B3DCC] font-medium">Status</label>
              <Form.Item name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
              </Form.Item>
            </div>
          </div>
          <div className="flex justify-end mt-8">
            <LoadableButton
              className="bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500"
              disabled={isSubmitLoading}
              type="submit"
              lable={selectedItem ? "Update" : "Submit"}
              isLoading={isSubmitLoading}
              loadingLable={selectedItem ? "Updating..." : "Adding..."}
            />
          </div>
        </Form>
      </ModalComponent>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        setIsOpen={(open) => {
          setDeleteModalOpen(open);
          if (!open) setItemToDelete(null);
        }}
        message="Are you sure you want to delete this education section?"
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
}
